import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { CompanyDocumentDetailQueryResponse } from '@area-restrita/api-client/model/companyDocumentDetailQueryResponse';
import {
  statusOptions,
  reasonOptions,
  reasonsFieldsMap,
  type ReasonOption,
  type Status,
} from '@area-restrita/shared/document/document-requests';

import InlineInput from '@/components/InlineInput';
import Select, { SelectOption } from '@/components/Select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Form, FormField } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import FileInput from '@/components/FileInput';

interface IRequestChangesFormSchema {
  status: Status;
  dateForApproval: string;
  observations: string;
  justification?: string;
  file: File | undefined;
  reason: ReasonOption;
  reasonCustomFields: Record<string, string>;
}

interface IProps {
  companyDocument: CompanyDocumentDetailQueryResponse;
  defaultValues?: Partial<IRequestChangesFormSchema>;
}

const RequestChanges: React.FC<IProps> = ({
  defaultValues = {
    status: { label: 'Aguardando aprovação', value: 'pending' },
    reason: { label: 'Troca de turno', value: 'shift_change' },
    reasonCustomFields: {},
  },
}) => {
  const form = useForm<IRequestChangesFormSchema>({
    defaultValues,
  });

  const { control, watch } = form;

  const status = watch('status');
  const reason = watch('reason');

  const readOnly = status.value !== 'request';

  const customFields = useMemo(
    () => reasonsFieldsMap[reason.value].fields,
    [reason],
  );

  return (
    <Form {...form}>
      <Card>
        <CardHeader>
          <CardTitle></CardTitle>
          <CardDescription></CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            {status.value !== 'request' && (
              <FormField
                control={form.control}
                name="file"
                render={({ field }) => (
                  <InlineInput title="Arquivo" as={FileInput} {...field} />
                )}
              />
            )}

            <FormField
              control={control}
              name="status"
              render={({ field }) => (
                <InlineInput
                  title="Status"
                  as={(props: {
                    value: SelectOption;
                    onChange: (value: SelectOption) => void;
                  }) => (
                    <Select
                      options={statusOptions}
                      {...props}
                      multiple={false}
                    />
                  )}
                  // disabled={readOnly}
                  {...field}
                />
              )}
            />

            {status.value !== 'request' && (
              <FormField
                control={control}
                name="justification"
                render={({ field }) => (
                  <InlineInput
                    title="Justificativa"
                    as={Textarea}
                    // disabled={readOnly}
                    {...field}
                  />
                )}
              />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <FormField
              control={control}
              name="reason"
              render={({ field }) => (
                <InlineInput
                  title="Motivo"
                  as={(props: {
                    value: SelectOption;
                    onChange: (value: SelectOption) => void;
                  }) => (
                    <Select
                      options={reasonOptions}
                      {...props}
                      multiple={false}
                    />
                  )}
                  // disabled={readOnly}
                  {...field}
                />
              )}
            />
            {customFields.map(({ name, label }) => (
              <FormField
                control={control}
                name={`reasonCustomFields.${name}`}
                render={({ field }) => (
                  <InlineInput
                    title={label}
                    disabled={readOnly}
                    // disabled={isLoading}
                    {...field}
                  />
                )}
              />
            ))}
          </div>
          <div className="flex flex-col gap-2">
            <FormField
              control={control}
              name="dateForApproval"
              render={({ field }) => (
                <InlineInput
                  title="Data para aprovação"
                  disabled={readOnly}
                  // disabled={isLoading}
                  {...field}
                />
              )}
            />
            <FormField
              control={control}
              name="observations"
              render={({ field }) => (
                <InlineInput
                  title="Observações"
                  as={Textarea}
                  disabled={readOnly}
                  // disabled={isLoading}
                  {...field}
                />
              )}
            />
          </div>
        </CardContent>
      </Card>
    </Form>
  );
};

export default RequestChanges;
